import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import React, { useState } from 'react';
import { change, FieldArray } from 'redux-form';
import {
    faWindowClose,
    faPlusCircle,
    faRedo,
    faChevronDown,
    faChevronUp,
    faEye,
    faEyeSlash,
    faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import styles from './assets/program-array-fields.module.scss';
import RenderTargetArray from './RenderTargetArray';
import { FieldTextArea } from '../../../../../components/redux-form/fields/FieldTextArea';
import GeneralEnumService from '../../../../../enumerables/general-enum';
import { getValFromEnum } from '../../../../../components/redux-form/utils/dropdown-values';
import { Button, Modal } from 'react-bootstrap';
import { Droppable, Draggable, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { ensureCid, genCid, getNextSortOrder, stableKey } from './dnd/cid';
import { useRegisterDroppable } from './dnd/DndRegistry';

type RenderShortTermArrayProps = any;

const RenderFieldArray = (props: RenderShortTermArrayProps) => {
    const { fields, remove, isHiddenShow } = props;

    const EnumSet = GeneralEnumService ? GeneralEnumService.getEnum('ProgramPrompt') : {};
    const programPromptItems: SelectObject[] = getValFromEnum(EnumSet);
    const prompts = programPromptItems.map((prompt: any) => ({
        id: prompt.value,
        title: prompt.label,
        prompt: prompt.value,
        checked: false,
    }));

    const droppableId = `shortterms-${fields.name}`;
    useRegisterDroppable(droppableId, fields);

    return (
        <Droppable droppableId={droppableId} type="shortterms">
            {(dropProvided) => (
                <ul
                    className={classNames(styles.ul, remove ? styles.removeStyle : styles.shortTermWrap)}
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                >
                    <li className={classNames(styles.addBtnLi)}>
                        <button
                            type="button"
                            className={classNames('btn', 'btn-outline-info', 'mr-3', styles.addBtn)}
                            onClick={() =>
                                fields.push({
                                    clientId: genCid(), // shortterm
                                    sortOrder: getNextSortOrder(fields),
                                    targets: [
                                        {
                                            clientId: genCid(), // target
                                            sortOrder: 0,
                                            programTargetOptions: [{ name: '' }],
                                            programTargetPrompts: prompts,
                                        },
                                    ],
                                })
                            }
                        >
                            <FontAwesomeIcon icon={faPlusCircle} /> <span>Add short term goal</span>
                        </button>
                    </li>

                    {fields.map((member: any, index: number, array: any) => {
                        const row = array.get(index);
                        //if (row) ensureCid(row);

                        const shorttermKey = row ? stableKey(row, 'shortterm') : `shortterm-fallback-${index}`;

                        return (
                            <Draggable draggableId={shorttermKey} index={index} key={shorttermKey}>
                                {(dragProvided, snapshot) => (
                                    <li
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        style={dragProvided.draggableProps.style}
                                        className={classNames(
                                            'w-100',
                                            styles.itemLi,
                                            snapshot.isDragging ? styles.dragging : '',
                                        )}
                                    >
                                        <RenderSubFields
                                            member={member}
                                            isHiddenShow={isHiddenShow}
                                            remove={remove}
                                            index={index}
                                            fields={array}
                                            isLast={index + 1 === fields.length}
                                            isNotFirst={!!index}
                                            dragHandleProps={dragProvided.dragHandleProps}
                                        />
                                    </li>
                                )}
                            </Draggable>
                        );
                    })}

                    {dropProvided.placeholder}
                </ul>
            )}
        </Droppable>
    );
};

type RenderSubFieldsProps = {
    member: any;
    index: number;
    fields: any;
    remove: boolean;
    isLast: boolean;
    isNotFirst: boolean;
    isHiddenShow: string;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

const RenderSubFields: React.FunctionComponent<RenderSubFieldsProps> = ({
    member,
    isHiddenShow,
    remove,
    index,
    fields,
    isLast,
    isNotFirst,
    dragHandleProps,
}) => {
    const dispatch = useDispatch();
    const [changeSt, setChanged] = useState(false);

    const row = fields.get(index);
    const [isHide, setHide] = useState(!!row?.isCollapsed);
    const [isHiddenLocal, setIsHiddenLocal] = useState(+row?.isHidden === 1);

    const [openedDeleteST, setOpenedDeleteST] = useState(false);

    const handleClose = () => setOpenedDeleteST(false);

    const deleteRow = () => {
        handleClose();
        if (!row?.id) {
            fields.remove(index);
        } else {
            dispatch(change('program-book', `${member}.status`, 2));
        }
        setTimeout(() => setChanged(!changeSt), 200);
    };

    const backRow = () => {
        dispatch(change('program-book', `${member}.status`, 1));
        setTimeout(() => setChanged(!changeSt), 200);
    };

    const hideRow = () => {
        dispatch(change('program-book', `${member}.isHidden`, 1));
        setIsHiddenLocal(true);
    };

    const showRow = () => {
        dispatch(change('program-book', `${member}.isHidden`, 0));
        setIsHiddenLocal(false);
    };

    const creditStyle = { display: 'none' };
    const isApplied: boolean = +row?.status === 3;

    if (isHiddenShow === '0' && isHiddenLocal) return null;

    return (
        <div className={isHiddenLocal ? styles.hiddenItem : undefined}>
            <div className={classNames(styles.wrappedFields)}>
                <FieldInput
                    name={`${member}.title`}
                    type="text"
                    disabled={isApplied}
                    wrapperConfig={{
                        classes: {
                            inputWrapper: 'mr-3 w-66 ' + classNames(styles.wFullWidth),
                            label: classNames(styles.boldLabel),
                        },
                        label: 'Short term goal:',
                    }}
                />

                <FieldTextArea
                    name={`${member}.description`}
                    disabled={isApplied}
                    wrapperConfig={{
                        classes: { inputWrapper: 'mr-3 w-33 ' + classNames(styles.wFullWidth) },
                        label: 'Description:',
                    }}
                />

                <div className={styles.actionBar}>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => {
                            setHide(!isHide);
                            dispatch(change('program-book', `${member}.isCollapsed`, !isHide));
                        }}
                    >
                        <FontAwesomeIcon icon={isHide ? faChevronDown : faChevronUp} />
                    </button>
                    <div {...(dragHandleProps || {})} className={styles.dragHandle} title="Click and drag to reorder">
                        <FontAwesomeIcon icon={faGripVertical} />
                    </div>
                    {isHiddenLocal ? (
                        <button
                            type="button"
                            className={styles.actionBtnSuccess}
                            title="Open Short Term"
                            onClick={showRow}
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={styles.actionBtnDanger}
                            title="Close Short Term"
                            onClick={hideRow}
                        >
                            <FontAwesomeIcon icon={faEyeSlash} />
                        </button>
                    )}

                    {((!isApplied && isNotFirst) || row?.id) && +row?.status !== 2 ? (
                        <>
                            <button
                                type="button"
                                className={styles.actionBtnDanger}
                                title="Remove Short-term"
                                onClick={() => (row?.id ? setOpenedDeleteST(100 + row.id) : deleteRow())}
                            >
                                <FontAwesomeIcon icon={faWindowClose} />
                            </button>
                            <Modal show={openedDeleteST === 100 + row?.id} onHide={handleClose}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Confirmation</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to delete the short term goal? All data, collected for the
                                    targets within this short term goal, will be lost.
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={handleClose}>
                                        No
                                    </Button>
                                    <Button variant="success" onClick={deleteRow}>
                                        Yes
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </>
                    ) : row?.id && +row?.status === 2 ? (
                        <button
                            type="button"
                            className={styles.actionBtnSuccess}
                            title="Restore Short-term"
                            onClick={backRow}
                        >
                            <FontAwesomeIcon icon={faRedo} />
                        </button>
                    ) : null}
                </div>
            </div>

            <div style={isHide ? { display: 'none' } : undefined}>
                <FieldArray
                    name={`${member}.targets`}
                    isHiddenShow={isHiddenShow}
                    remove={+row?.status === 2 || remove}
                    component={RenderTargetArray}
                />
            </div>
        </div>
    );
};

export default RenderFieldArray;
